// Comment units (approved before M4): comments anchor on the M2 units, i.e. list items
// (recursively) and table rows, while the review keeps showing whole blocks.
import { toString } from 'mdast-util-to-string';
import { unitize, type Unit } from '../markdown/units';
import { anchorFor, type Anchor, type Hunk } from './anchors';
import type { FileDiff } from './blockDiff';

export type CommentUnit = {
  kind: Unit['kind'];
  startLine: number;
  endLine: number;
  /** Plain text of the unit: quoted in file-level comments, used in button labels. */
  text: string;
  anchor: Anchor;
};

const lineAt = (md: string, offset: number) => md.slice(0, offset).split('\n').length;

export type BlockUnits = {
  /** List items / table rows (or the block itself). */
  units: CommentUnit[];
  /** The whole display block, used when per-unit buttons can't be aligned. */
  block: CommentUnit;
};

/** Units of each display block (index into diff.items), in document order. */
export function commentUnits(head: string, diff: FileDiff, hunks: Hunk[], changed: Set<number>): Map<number, BlockUnits> {
  const units = unitize(head, diff.headTree);
  const out = new Map<number, BlockUnits>();
  diff.items.forEach((it, i) => {
    if (!('head' in it)) return; // removed blocks: nothing on the head side to comment on
    const bStart = it.head.node.position!.start.offset!, bEnd = it.head.node.position!.end.offset!;
    const inBlock = units.filter((u) => u.start >= bStart && u.end <= bEnd);
    const list = (inBlock.length ? inBlock : [{ kind: 'block' as const, start: bStart, end: bEnd, node: it.head.node }]).map((u) => {
      const startLine = lineAt(head, u.start), endLine = lineAt(head, u.end);
      return { kind: u.kind, startLine, endLine, text: toString(u.node).replace(/\s+/g, ' ').trim(), anchor: anchorFor(startLine, endLine, hunks, changed) };
    });
    const bs = lineAt(head, bStart), be = lineAt(head, bEnd);
    const block: CommentUnit = { kind: 'block', startLine: bs, endLine: be, text: toString(it.head.node).replace(/\s+/g, ' ').trim(), anchor: anchorFor(bs, be, hunks, changed) };
    out.set(i, { units: list, block });
  });
  return out;
}
